import React, { useState, useEffect, useRef } from 'react';
import { Button, ButtonProps, Dialog, DialogActions, DialogContent, CircularProgress } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from '../../store';

export type MfaMode = 'NONE' | 'IF_ENABLED' | 'REQUIRED';

const CODE_LENGTH = 6;

type Props = {
    open: boolean;
    title?: string;
    titleNode?: React.ReactNode;
    message?: React.ReactNode;
    abortText?: React.ReactNode;
    submitText?: React.ReactNode;
    submitColor?: ButtonProps['color'];
    closeButton?: boolean;
    onAbort?: () => void;
    onSubmit?: (mfaCode?: string) => void | Promise<void>;
    submitDisabled?: boolean;
    abortDisabled?: boolean;
    children?: React.ReactNode | ((mfaCode: string, isMfaValid: boolean) => React.ReactNode);
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    minHeight?: string;
    maxHeight?: string;
    disablePaddingTop?: boolean;
    fullWidthSubmit?: boolean;
    loading?: boolean;
    mfaMode?: MfaMode;
};

const BaseModal: React.FC<Props> = ({
                                        open,
                                        title,
                                        titleNode,
                                        message,
                                        abortText,
                                        submitText,
                                        submitColor = 'primary',
                                        closeButton = true,
                                        onAbort,
                                        onSubmit,
                                        submitDisabled = false,
                                        abortDisabled = false,
                                        children,
                                        maxWidth = 'sm',
                                        minHeight,
                                        maxHeight,
                                        disablePaddingTop = false,
                                        fullWidthSubmit = false,
                                        loading = false,
                                        mfaMode = 'NONE',
                                    }) => {
    // Check if any title is provided
    const hasTitle = Boolean(title || titleNode);
    const showClose = Boolean(closeButton && onAbort);

    // Fetch user 2FA state directly inside component
    const isUserMfaActive = useSelector((state: RootState) => state.auth.user?.hasMfa ?? false);

    // Internal state handling for 2FA step transition
    const [mfaCode, setMfaCode] = useState('');
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

    // Reset step state when modal is toggled
    useEffect(() => {
        if (!open) {
            setMfaCode('');
        }
    }, [open]);

    // Check conditions for 2FA requirement
    const isMfaRequiredAndDisabled = mfaMode === 'REQUIRED' && !isUserMfaActive;
    const showMfaInput = (mfaMode === 'REQUIRED' || mfaMode === 'IF_ENABLED') && isUserMfaActive;

    // Validates whether MFA requirements are met
    const isMfaValid = !isMfaRequiredAndDisabled && (!showMfaInput || mfaCode.length === CODE_LENGTH);

    // Handle initial submit click and 2FA validation rules
    const handlePreSubmit = () => {
        if (isMfaRequiredAndDisabled) return;
        onSubmit?.(showMfaInput ? mfaCode : undefined);
    };

    // Handle input field changes for 6 digit MFA code
    const handleCodeChange = (value: string, index: number) => {
        if (value && !/^\d+$/.test(value)) return;

        const newCodeArr = mfaCode.split('');
        newCodeArr[index] = value.substring(value.length - 1);
        const updatedCode = newCodeArr.join('');
        setMfaCode(updatedCode);

        if (value && index < CODE_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    // Handle backspace and enter key navigation inside OTP inputs
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !mfaCode[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'Enter' && (!showMfaInput || mfaCode.length === CODE_LENGTH)) {
            handlePreSubmit();
        }
    };

    // Handle pasting complete 6 digit OTP code
    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, CODE_LENGTH).replace(/[^0-9]/g, '');
        setMfaCode(pastedData);

        const nextFocusIndex = Math.min(pastedData.length, CODE_LENGTH - 1);
        inputRefs.current[nextFocusIndex]?.focus();
    };

    // Calculate disabled state for action submit button
    const isSubmitDisabled = submitDisabled || loading || !isMfaValid;

    return (
        <Dialog
            open={open}
            onClose={onAbort ?? undefined}
            maxWidth={maxWidth}
            fullWidth
            disableScrollLock={true}
            PaperProps={{
                className: "dark:bg-neutral-950 bg-light",
                sx: {
                    ...(minHeight ? { minHeight } : {}),
                    ...(maxHeight ? { maxHeight } : {}),
                }
            }}
        >
            {/* Conditional header container */}
            {hasTitle ? (
                <div className="!p-[12px] !pb-[6px]">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                            {titleNode || <div className="text-xl font-semibold">{title}</div>}
                        </div>
                        {showClose && (
                            <IconButton
                                aria-label="close"
                                onClick={onAbort}
                                className="text-gray-400 hover:text-white !p-1"
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </div>
                </div>
            ) : (
                /* Absolute close button when no title exists to prevent vertical layout shifting */
                showClose && (
                    <IconButton
                        aria-label="close"
                        onClick={onAbort}
                        className="!absolute !right-[1px] !top-[1px] text-gray-400 hover:text-white !p-1 z-10"
                    >
                        <CloseIcon />
                    </IconButton>
                )
            )}

            {/* Content container with exact 12px padding around */}
            <DialogContent className={`md:!px-[20px] !px-[8px] md:!pb-[4px] !pb-[0px] ${disablePaddingTop || !hasTitle ? "md:!pt-[24px] !pt-[8px]" : "!pt-[6px]"} scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600`}>
                {message && <div className="mb-3">{message}</div>}
                {typeof children === 'function' ? children(mfaCode, isMfaValid) : children}
            </DialogContent>

            {/* Action container with exact 12px padding around */}
            <DialogActions className={`!p-[12px] !pt-[6px] flex flex-col sm:flex-row items-stretch sm:items-center !justify-between w-full gap-3 ${fullWidthSubmit ? "!flex-col !items-stretch" : ""}`}>
                {/* Render 2FA inputs or warning message on the left side */}
                {(isMfaRequiredAndDisabled || showMfaInput) && (
                    <div className="flex items-center justify-center sm:justify-start ml-2">
                        {isMfaRequiredAndDisabled && (
                            <div className="text-xs text-red-500 font-medium">
                                2FA required.&nbsp;
                                <Link to="/profile/security" className="underline hover:text-red-400 font-bold">
                                    Enable in Settings
                                </Link>
                            </div>
                        )}
                        {showMfaInput && (
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mr-1">2FA:</span>
                                {Array.from({ length: CODE_LENGTH }).map((_, i) => (
                                    <input
                                        key={i}
                                        ref={(el) => (inputRefs.current[i] = el)}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={mfaCode[i] || ''}
                                        onChange={(e) => handleCodeChange(e.target.value, i)}
                                        onKeyDown={(e) => handleKeyDown(e, i)}
                                        onPaste={handlePaste}
                                        className="w-7 h-9 text-center text-sm font-bold rounded border border-neutral-300 dark:border-neutral-700 bg-transparent dark:text-white outline-none transition-all focus:border-primary-500"
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Render action buttons on the right side */}
                <div className={`flex items-center justify-end gap-2 mr-2 ${fullWidthSubmit ? "w-full flex-col" : "ml-auto"}`}>
                    {abortText && onAbort && (
                        <Button
                            onClick={onAbort}
                            className="text-gray-400 hover:text-white"
                            disabled={abortDisabled || loading}
                            fullWidth={fullWidthSubmit}
                        >
                            {abortText}
                        </Button>
                    )}
                    {submitText && onSubmit && (
                        <Button
                            onClick={handlePreSubmit}
                            variant="contained"
                            color={submitColor}
                            disabled={isSubmitDisabled}
                            fullWidth={fullWidthSubmit}
                            className={fullWidthSubmit ? "text-center" : ""}
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                        >
                            {submitText}
                        </Button>
                    )}
                </div>
            </DialogActions>
        </Dialog>
    );
};

export default BaseModal;